import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Icon,
    Label,
    Textarea,
} from "@elit-hair/elit-ui";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbAlertTriangle, TbCheck, TbLoader2 } from "react-icons/tb";

import { useCreateIncident } from "@/queries/incidents.queries";

import { collectAudioDeviceEventLog, collectAudioDevices, collectLocalStorage } from "../utils/collect-incident-data";
import { TrendingIncidents } from "./trending-incidents";

type ReportIncidentDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

export function ReportIncidentDialog({ open, onOpenChange }: ReportIncidentDialogProps) {
    const { t } = useTranslation();
    const [description, setDescription] = useState("");
    const mutation = useCreateIncident();

    const handleSubmit = useCallback(async () => {
        if (!description.trim()) return;

        // Collect browser context for the report, but never let it block the submission:
        // if anything throws, we still want the user's message to reach the backend.
        let localStorageSnapshot: Record<string, string> = {};
        let audioDevices: Awaited<ReturnType<typeof collectAudioDevices>> | undefined;
        let audioDeviceLog: ReturnType<typeof collectAudioDeviceEventLog> = [];

        try {
            localStorageSnapshot = collectLocalStorage();
            audioDevices = await collectAudioDevices();
            audioDeviceLog = collectAudioDeviceEventLog();
        } catch {
            // Ignore context-collection failures so the incident can still be reported.
        }

        mutation.mutate(
            {
                description: description.trim(),
                data: {
                    "local-storage": localStorageSnapshot,
                    "audio-devices": audioDevices,
                    "audio-device-log": audioDeviceLog,
                },
            },
            {
                onSuccess: () => {
                    setDescription("");
                    setTimeout(() => {
                        onOpenChange(false);
                        mutation.reset();
                    }, 1500);
                },
            }
        );
    }, [description, mutation, onOpenChange]);

    const handleOpenChange = useCallback(
        (next: boolean) => {
            if (!next && !mutation.isPending) {
                mutation.reset();
            }
            onOpenChange(next);
        },
        [mutation, onOpenChange]
    );

    const errorMessage = mutation.error instanceof Error ? mutation.error.message : t("incident_dialog_failed");

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-lg overflow-hidden border-border/80 bg-card/95 shadow-xl backdrop-blur">
                <DialogHeader className="min-w-0 space-y-1">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">
                            <Icon icon={TbAlertTriangle} size={20} />
                        </div>
                        <div className="min-w-0">
                            <DialogTitle className="text-lg">{t("incident_dialog_title")}</DialogTitle>
                            <DialogDescription className="text-muted-foreground">
                                {t("incident_dialog_description")}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <div className="grid min-w-0 gap-4 py-2">
                    {/* Trending */}
                    <TrendingIncidents enabled={open} />

                    <div className="grid min-w-0 gap-2">
                        <Label htmlFor="incident-description" className="text-sm font-medium">
                            {t("incident_dialog_label")}
                        </Label>
                        <Textarea
                            id="incident-description"
                            placeholder={t("incident_dialog_placeholder")}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="min-h-[120px] w-full resize-y whitespace-pre-wrap wrap-break-word border-border/80 bg-background/80 placeholder:text-muted-foreground"
                            disabled={mutation.isPending}
                            autoFocus
                        />
                    </div>
                    {mutation.isError && (
                        <p className="wrap-break-word rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                            {errorMessage}
                        </p>
                    )}
                    {mutation.isSuccess && (
                        <p className="flex items-center gap-2 rounded-md bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-400">
                            <Icon icon={TbCheck} /> {t("incident_dialog_success")}
                        </p>
                    )}
                </div>
                <DialogFooter className="gap-2 sm:gap-0">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                        disabled={mutation.isPending}
                    >
                        {t("cancel")}
                    </Button>
                    <Button type="button" onClick={handleSubmit} disabled={!description.trim() || mutation.isPending}>
                        {mutation.isPending ? (
                            <>
                                <Icon icon={TbLoader2} className="animate-spin" />
                                {t("incident_dialog_sending")}
                            </>
                        ) : (
                            t("incident_dialog_send")
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
